import { useEffect, useState, type ReactNode } from "react";
import { asError, eventBus } from "@optimyl/events";
import { useAuthContext } from "./SessionProvider";

export interface SessionGuardProps {
  /** What the app shows while the session loads or the browser leaves to sign in. */
  busyView: ReactNode;
  /** What the app shows when a sign-in can't start, given the message. */
  errorView: (message: string) => ReactNode;
  children: ReactNode;
}

export const SignInUnavailableMessage = "We couldn't reach the Optimyl sign-in. Try again in a moment.";

/**
 * A route that needs a signed-in person: anyone else is sent to sign in and comes back to this page. With sign-in off
 * (a local run) the children render as they are. A sign-in that can't start (the identity server unreachable) is
 * reported and shown with the app's error view, whose way back tries again. Built by createAuth.
 */
export function SessionGuard({ busyView, errorView, children }: SessionGuardProps) {
  const { isEnabled, isAuthenticated, isLoading, login } = useAuthContext();
  const [hasSignInFailed, setHasSignInFailed] = useState(false);

  useEffect(() => {
    if (!isEnabled || isLoading || isAuthenticated) return;
    login().catch((err: unknown) => {
      eventBus.publish("error", { error: asError(err), context: "AuthGuard.signIn" });
      setHasSignInFailed(true);
    });
  }, [isEnabled, isLoading, isAuthenticated, login]);

  if (!isEnabled || (!isLoading && isAuthenticated)) return <>{children}</>;
  if (hasSignInFailed) return <div role="alert">{errorView(SignInUnavailableMessage)}</div>;
  return <>{busyView}</>;
}
